import { Skeleton } from '@mui/material';

// 首次加载和筛选刷新均按一页 36 个商品版位占位。
const PRODUCT_SKELETON_COUNT = 36;

// PC 端骨架卡片 — aspect-[504/378] 横向
const PCSkeletonCard = () => (
  <div className="w-full">
    <div className="w-full aspect-[504/378] rounded-16 bg-gray-100 overflow-hidden">
      <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
    </div>
    <div className="mt-16 mb-12">
      <Skeleton width="100%" height={18} animation="wave" />
    </div>
    <div className="flex items-center justify-between">
      <Skeleton width="40%" height={16} animation="wave" />
      <Skeleton width={40} height={14} animation="wave" variant="rounded" />
    </div>
  </div>
);

// M 端骨架卡片 — aspect-[186/223] 竖向
const MSkeletonCard = () => (
  <div className="w-full">
    <div className="w-full aspect-[186/223] bg-gray-100 overflow-hidden">
      <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
    </div>
    <div className="mt-10 mb-8 px-8">
      <Skeleton width="100%" height={14} animation="wave" />
      <div className="flex items-center justify-between mt-6">
        <Skeleton width="40%" height={14} animation="wave" />
      </div>
    </div>
  </div>
);

// PC 端左侧筛选骨架 — 匹配真实 GoodsFilter 布局
const FilterSidebarSkeleton = () => (
  <div className="max-[920px]:hidden">
    {/* 面包屑 */}
    <div className="sticky top-[100px] z-3 h-[40px] pl-48 py-12 w-[288px]">
      <Skeleton width={120} height={14} animation="wave" />
    </div>
    {/* 筛选面板 */}
    <div className="w-[304px] left-0 z-[2] h-screen sticky top-[140px]">
      <div className="h-full bg-white overflow-y-auto">
        {/* Filters 标题 */}
        <div className="pt-[16px] pl-48 pb-24">
          <Skeleton width={80} height={24} animation="wave" />
        </div>
        <div className="pl-48 pr-24 pb-[350px]">
          {/* Section 1: 颜色色块 (5列) */}
          <div className="border-b border-gray-200 mt-24 first:mt-0">
            <div className="pt-10 pb-20 h-[50px] flex items-center">
              <Skeleton width={90} height={16} animation="wave" />
            </div>
            <div className="grid grid-cols-5 gap-24 pb-20">
              {Array.from({ length: 10 }).map((_, j) => (
                <Skeleton key={j} variant="circular" width={20} height={20} animation="wave" />
              ))}
            </div>
          </div>

          {/* Section 2: 复选框选项 (单列) */}
          <div className="border-b border-gray-200 mt-24">
            <div className="pt-10 pb-20 h-[50px] flex items-center">
              <Skeleton width={100} height={16} animation="wave" />
            </div>
            <div className="grid grid-cols-1 gap-[16px] pb-20">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="flex items-center gap-12 h-[20px]">
                  <Skeleton
                    variant="rounded"
                    width={16}
                    height={16}
                    animation="wave"
                    sx={{ borderRadius: '3px' }}
                  />
                  <Skeleton width={70 + j * 10} height={14} animation="wave" />
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: 复选框选项 */}
          <div className="border-b border-gray-200 mt-24">
            <div className="pt-10 pb-20 h-[50px] flex items-center">
              <Skeleton width={70} height={16} animation="wave" />
            </div>
            <div className="grid grid-cols-1 gap-[16px] pb-20">
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="flex items-center gap-12 h-[20px]">
                  <Skeleton
                    variant="rounded"
                    width={16}
                    height={16}
                    animation="wave"
                    sx={{ borderRadius: '3px' }}
                  />
                  <Skeleton width={80 + j * 15} height={14} animation="wave" />
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: 价格区间 (滑块) */}
          <div className="border-b border-gray-200 mt-24">
            <div className="pt-10 pb-20 h-[50px] flex items-center">
              <Skeleton width={50} height={16} animation="wave" />
            </div>
            <div className="pb-20">
              <Skeleton
                variant="rounded"
                width="100%"
                height={3}
                animation="wave"
                sx={{ borderRadius: '2px' }}
              />
              <div className="flex justify-between mt-10">
                <Skeleton width={36} height={12} animation="wave" />
                <Skeleton width={36} height={12} animation="wave" />
              </div>
            </div>
          </div>

          {/* Section 5: 复选框选项 */}
          <div className="border-b border-gray-200 mt-24">
            <div className="pt-10 pb-20 h-[50px] flex items-center">
              <Skeleton width={60} height={16} animation="wave" />
            </div>
            <div className="grid grid-cols-1 gap-[16px] pb-20">
              {Array.from({ length: 2 }).map((_, j) => (
                <div key={j} className="flex items-center gap-12 h-[20px]">
                  <Skeleton
                    variant="rounded"
                    width={16}
                    height={16}
                    animation="wave"
                    sx={{ borderRadius: '3px' }}
                  />
                  <Skeleton width={60 + j * 20} height={14} animation="wave" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/**
 * 完整页面骨架屏 — 用于 loading.tsx 路由级加载
 * 纯 CSS 响应式，不需要 isWeb prop
 */
export default function GoodsListSkeleton() {
  return (
    <div className="relative w-full min-h-screen flex">
      {/* PC 左侧筛选区 */}
      <FilterSidebarSkeleton />

      {/* 右侧内容区 */}
      <div className="min-h-screen w-[calc(100vw-304px)] max-[920px]:w-full">
        <div className="px-40 pb-40 max-[920px]:px-0">
          {/* Banner */}
          <div className="pt-16 max-[920px]:pt-0">
            {/* PC Banner: 16/5 圆角 */}
            <div className="min-[920px]:block max-[920px]:hidden">
              <div className="w-full aspect-[16/5] rounded-12 bg-gray-100 overflow-hidden">
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
              </div>
            </div>
            {/* M Banner: 750/320 无圆角 */}
            <div className="max-[920px]:block min-[920px]:hidden">
              <div className="w-full aspect-[750/320] bg-gray-100 overflow-hidden">
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
              </div>
            </div>
          </div>

          {/* 排序栏 — 匹配真实 SortFilterBar 布局 */}
          <div className="sticky bg-white z-[3] min-[920px]:top-[100px] max-[920px]:border-b max-[920px]:border-gray-200 max-[920px]:top-[11.733vw]">
            <div className="flex items-center min-[920px]:pt-20 min-[920px]:pb-14 max-[920px]:h-[36px] max-[920px]:px-[14px] max-[920px]:justify-between">
              {/* 排序文字 */}
              <Skeleton width={80} height={20} animation="wave" />
              {/* PC: 结果数 */}
              <div className="ml-auto items-center gap-16 hidden min-[920px]:flex">
                <Skeleton width={120} height={16} animation="wave" />
                <Skeleton width={80} height={20} animation="wave" />
              </div>
              {/* M: 筛选按钮 + 列数切换 */}
              <div className="items-center flex min-[920px]:hidden">
                <div className="flex items-center">
                  <Skeleton width={40} height={18} animation="wave" />
                  <div className="ml-[3px]">
                    <Skeleton
                      variant="rounded"
                      width={20}
                      height={20}
                      animation="wave"
                      sx={{ borderRadius: '10px' }}
                    />
                  </div>
                </div>
                <div className="border-l border-[#eeeeee] pl-[10px] ml-[13px]">
                  <Skeleton
                    variant="rounded"
                    width={20}
                    height={20}
                    animation="wave"
                    sx={{ borderRadius: '4px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* M端: 结果数行 */}
          <div className="max-[920px]:block min-[920px]:hidden">
            <div className="h-[38px] leading-[38px] pl-14">
              <Skeleton width={100} height={14} animation="wave" />
            </div>
          </div>

          {/* PC 商品网格: 3列，响应式变2列 */}
          <div className="min-[920px]:grid hidden grid-cols-3 gap-y-[64px] gap-x-[16px] max-[1200px]:grid-cols-2">
            {Array.from({ length: PRODUCT_SKELETON_COUNT }).map((_, i) => (
              <PCSkeletonCard key={i} />
            ))}
          </div>

          {/* M 商品网格: 固定2列 */}
          <div className="max-[920px]:grid hidden grid-cols-2 gap-x-[4px] gap-y-[40px]">
            {Array.from({ length: PRODUCT_SKELETON_COUNT }).map((_, i) => (
              <MSkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface InlineProductGridSkeletonProps {
  isWeb: boolean;
  column?: number;
}

/**
 * 仅商品网格骨架 — 用于客户端数据更新时内联显示
 */
export function InlineProductGridSkeleton({ isWeb, column = 2 }: InlineProductGridSkeletonProps) {
  const getGridClass = () => {
    if (isWeb) {
      return 'grid grid-cols-3 gap-y-[64px] gap-x-[16px] max-[1200px]:grid-cols-2 max-[640px]:flex max-[640px]:flex-col';
    }
    if (column === 1) {
      return 'flex flex-col px-14 gap-y-[40px]';
    }
    return 'grid grid-cols-2 gap-x-[4px] gap-y-[40px]';
  };

  return (
    <div className={`min-h-[30vh] pb-40 ${getGridClass()}`}>
      {Array.from({ length: PRODUCT_SKELETON_COUNT }).map((_, i) =>
        isWeb ? <PCSkeletonCard key={i} /> : <MSkeletonCard key={i} />,
      )}
    </div>
  );
}
